import React, { useEffect, useState } from "react";
import {
  Modal as AntModal,
  Button,
  Form,
  Input,
  message,
  Select,
  Row,
  Col,
} from "antd";
import styled from "styled-components";
import api from "../../../../api/api";
import { useAtom } from "jotai";
import { orderCount } from "../../../../utils/atom";
import { useTranslation } from "react-i18next";

const { TextArea } = Input;
const { Option } = Select;

const StyledModal = styled(AntModal)`
  .ant-modal-content {
    border-radius: 12px;
    padding: 24px;
  }

  .ant-modal-header {
    border-bottom: none;
    padding: 0;
    margin-bottom: 4px;
  }

  .ant-modal-title {
    font-size: 20px;
    font-weight: 600;
  }

  .ant-modal-body {
    padding: 0;
  }

  .ant-modal-footer {
    display: none;
  }

  .ant-form-item {
    margin-bottom: 18px;
  }

  .ant-form-item-label > label {
    font-size: 14px;
    font-weight: 500;
    color: #6b7280;
  }

  /* Input + Select */
  .ant-input,
  .ant-select-selector {
    border-radius: 8px !important;
    min-height: 44px !important;
    border: 1px solid #e5e7eb !important;
  }

  .ant-input {
    padding: 10px 12px;
  }

  .ant-input:focus,
  .ant-input-focused,
  .ant-select-focused .ant-select-selector {
    border-color: #1859ff !important;
    box-shadow: none !important;
  }

  /* Textarea fix */
  textarea.ant-input {
    resize: none;
    min-height: 90px !important;
  }
`;

const Description = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 24px;
  line-height: 22px;
`;

const FooterWrapper = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 8px;
`;

const CancelButton = styled(Button)`
  flex: 1;
  height: 48px;
  border-radius: 8px;
  font-weight: 500;
`;

const SubmitButton = styled(Button)`
  flex: 1;
  height: 48px;
  border-radius: 8px;
  font-weight: 500;
`;
const DeliveryRequestModal = ({
  open,
  handleClose,
  order,
  successCallback,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [deliCheck, setDeliCheck] = useState();
  const [storeDomain,setStoreDomain] = useState();
  const [orderStatus, setOrderStatus] = useAtom(orderCount);
  const [businessInfo, setBusinessInfo] = useState();

  const {t} = useTranslation();

 useEffect(() => {
      getBusinessInformation();
}, []);

    const getBusinessInformation = () => {
      api
        .getBusinessInformation()
        .then(({ data }) => {
          setLoading(false);
          setBusinessInfo(data);
        })
        .catch((error) => {
          message.error(error);
        });
    };
  
  

  const getCheckSubDomain = () => {
         setLoading(true);
      api
        .getCheckSubDomain()
        .then(({ data }) => {
        setStoreDomain(data?.domain);
        setLoading(false);
        })
        .catch((error) => {
          message.error(error);
           setLoading(false);
        });
    };

  useEffect(() => {
      getCheckSubDomain();
  }, []);


  const getDeliveryCheck = () => {
    api
      .getDeliveryCheck()
      .then((res) => {
        setDeliCheck(res);
      })
      .catch((err) => message.error(err));
  };

  useEffect(() => {
    getDeliveryCheck();
  }, []);

useEffect(() => {
  if (open && order) {
    const toAddressParts = [
      order?.delivery_address?.address,
      order?.delivery_address?.township?.name?.primary,
      order?.delivery_address?.city?.name?.primary,
      order?.delivery_address?.country?.name?.primary,
    ].filter(Boolean);

    const values = {
      from_phone: businessInfo?.phone?.[0],
      from_address: businessInfo?.address?.en,
      to_phone: order?.delivery_address?.phone,
      to_address: toAddressParts.length ? toAddressParts.join(", ") : undefined,
      delivery_system_id: deliCheck?.data?.[0]?.provider?.id,
    };

    const cleanedValues = Object.fromEntries(
      Object.entries(values).filter(
        ([_, v]) => v !== undefined && v !== null && v !== ""
      )
    );

    form.setFieldsValue(cleanedValues);
  }

  if (!open) {
    form.resetFields();
    setLoading(false);
  }
}, [open, order, deliCheck, businessInfo]);

  const onFinish = async (values) => {
    if (loading) return;

    try {
      setLoading(true);

      const payload = {
        delivery_system_id: values.delivery_system_id,
        from_phone: values.from_phone,
        from_address: values.from_address,
        to_phone: values.to_phone,
        to_address: values.to_address,
        note: values.note,
        voucher_url: `${storeDomain}/order-voucher/${order?.id}`,
      };

      await api.createDeliveryRequest(order?.id, payload);

      message.success("Delivery request sent successfully");
      successCallback?.();
      handleClose();
      setOrderStatus(!orderStatus)
    } catch (error) {
      message.error(error?.message || "Failed to request delivery");
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledModal
      centered
      open={open}
      title={t('req_deli')}
      onCancel={() => !loading && handleClose()}
    >
      <Description>
        {t('deli_info_text')}
      </Description>

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          label={t('deli_system')}
          name="delivery_system_id"
          rules={[{ required: true, message: "Please select delivery system" }]}
        >
          <Select placeholder="Select delivery system">
            {deliCheck?.data?.map((item) => (
              <Option key={item.id} value={item?.provider?.id}>
                {item.provider?.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
               <Row gutter={[12, 12]} align="middle" justify="space-between">
          <Col span={12}>
            <Form.Item
              label={t('from_phone')}
              name="from_phone"
              rules={[{ required: true, message: "Please enter a phone number" }]}
            >
              <Input placeholder={t('enter_phone_number')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('to_phone')}
              name="to_phone"
              rules={[{ required: true, message: "Please enter a phone number" }]}
            >
              <Input placeholder={t('enter_phone_number')} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label={t('from_address')}
          name="from_address"
          rules={[{ required: true }]}
        >
          <TextArea placeholder={t('write_address_details')} rows={2} />
        </Form.Item>

        <Form.Item
          label={t('to_address')}
          name="to_address"
          rules={[{ required: true }]}
        >
          <TextArea placeholder={t('write_address_details')} rows={2} />
        </Form.Item>

        <Form.Item label={t('note')} name="note">
          <TextArea placeholder={t('write_note')} rows={2} />
        </Form.Item>

        <Form.Item>
         <FooterWrapper>
        <CancelButton onClick={handleClose}>
          {t('not_now')}
        </CancelButton>

        <SubmitButton
          type="primary"
          htmlType="submit"
          loading={loading}
        >
          {t('request')}
        </SubmitButton>
      </FooterWrapper>
        </Form.Item>
      </Form>
    </StyledModal>
  );
};

export default DeliveryRequestModal;